import React, { useCallback, useContext, useMemo, useState } from "react";

import {
  CampaignUtils,
  HelperUtils,
  HtmlSelectOption,
  RollResultContract,
  RollValueContract,
} from "@dndbeyond/character-rules-engine/es";
import { Dice, RollRequest } from "@dndbeyond/dice";
import { Platform } from "@dndbeyond/event-pipeline-lib/shared";
import { isMessage, useMessageBroker } from "@dndbeyond/game-log-components";
import { Context as DiceWorkerContext } from "@dndbeyond/pocket-dimension-dice/context";
import { DiceOperations } from "@dndbeyond/pocket-dimension-dice/enums";
import { instrumentDiceRoll } from "@dndbeyond/pocket-dimension-dice/helpers/bi/instrumentDiceRoll";
import { rollDice } from "@dndbeyond/pocket-dimension-dice/helpers/rollDice";
import { useShared3dDiceSettingStore } from "@dndbeyond/pocket-dimension-dice/hooks/useShared3dDiceSettingStore";
import { useUserDiceDataStore } from "@dndbeyond/pocket-dimension-dice/hooks/useUserDiceDataStore";

import { useAuth } from "~/contexts/Authentication";
import { useFeatureFlags } from "~/contexts/FeatureFlag";
import { useCharacterEngine } from "~/hooks/useCharacterEngine";

import { DataLoadingStatusEnum } from "../componentConstants";
import { Button, RemoveButton } from "../legacy";
import { TypeScriptUtils, DiceComponentUtils } from "../utils";
import { DiceRoll, DiceRollProps } from "./DiceRoll";

export interface DiceRollGroupProps {
  className: string;
  componentKey: string;
  diceRolls: Array<RollResultContract>;
  exclusiveOptions?: boolean;
  confirmButtonText?: string;
  onConfirm?: (
    groupKey: string,
    rollResults: Array<RollResultContract>
  ) => void;
  onUpdateGroup?: (
    groupKey: string,
    rollResults: Array<RollResultContract>
  ) => void;
  onRollError?: (errorMessage: string) => void;
  groupKey: string;
  nextGroupKey: string | null;
  showRemoveButton?: boolean;
  onRemoveGroup?: (groupKey: string, nextGroupKey: string | null) => void;
  onResetGroup?: (
    groupKey: string,
    rollResults: Array<RollResultContract>
  ) => void;
  onUpdateDiceRoll: (
    rollKey: string,
    properties: Omit<Partial<RollResultContract>, "rollKey">,
    nextRollKey: string | null
  ) => void;
  onSetRollStatus: (rollKey: string, status: DataLoadingStatusEnum) => void;
  rollStatusLookup: Record<string, DataLoadingStatusEnum>;
  options?: DiceRollProps["options"];
  diceRollRequest:
    | DiceRollProps["diceRollRequest"]
    | Array<DiceRollProps["diceRollRequest"]>;
}
const DiceRollGroup: React.FunctionComponent<DiceRollGroupProps> = ({
  className = "",
  componentKey,
  diceRolls,
  options,
  confirmButtonText = "Confirm",
  showRemoveButton = false,
  exclusiveOptions = false,
  diceRollRequest,
  groupKey,
  nextGroupKey,
  onUpdateGroup,
  onRemoveGroup,
  onRollError,
  onConfirm,
  onResetGroup,
  onSetRollStatus,
  onUpdateDiceRoll,
  rollStatusLookup,
}) => {
  const { env, hasPddErrored, isPddReady } = useContext(DiceWorkerContext);
  const { tacticalMapsShared3dDiceFlag } = useFeatureFlags();
  const { campaign } = useCharacterEngine();
  const gameId = campaign ? String(campaign.id) : undefined;
  const dmId = campaign
    ? String(CampaignUtils.getDmUserId(campaign))
    : undefined;
  const [groupRolls, setGroupRolls] = useState<
    Record<
      string,
      {
        rollKey: string;
        properties: Partial<RollResultContract>;
        nextRollKey: string | null;
      }
    >
  >({});
  const user = useAuth();
  const classNames = useMemo<Array<string>>(
    () => ["ddbc-dice-roll-group", className],
    [className]
  );

  const areRollsEmpty = useMemo<boolean>(
    () => !diceRolls.some((roll) => roll.rollTotal !== null),
    [diceRolls]
  );

  const areScoresUnassigned = useMemo<boolean>(
    () =>
      !diceRolls.some(
        (roll) => roll.assignedValue !== null && roll.assignedValue !== ""
      ),
    [diceRolls]
  );

  const assignedValues = useMemo<Array<string>>(() => {
    return diceRolls
      .map((roll) => roll.assignedValue)
      .filter(TypeScriptUtils.isNotNullOrUndefined);
  }, [diceRolls]);

  const generatedOptions = useMemo<Array<Array<HtmlSelectOption>>>(() => {
    if (!options) {
      return [];
    }

    return diceRolls.map((roll): Array<HtmlSelectOption> => {
      if (!exclusiveOptions) {
        return options;
      }

      return options.filter(
        (option) =>
          roll.assignedValue === option.value ||
          !assignedValues.includes(option.value.toString())
      );
    });
  }, [options, diceRolls, exclusiveOptions, assignedValues]);

  const generatedRollRequests = useMemo<Array<RollRequest>>(() => {
    let requests: Array<RollRequest> = [];
    for (let i = 0; i < diceRolls.length; i++) {
      if (!Array.isArray(diceRollRequest)) {
        requests.push(diceRollRequest);
      } else {
        if (diceRollRequest[i]) {
          requests.push(diceRollRequest[i]);
        } else {
          requests.push(diceRollRequest[0]);
        }
      }
    }

    return requests;
  }, [diceRollRequest, diceRolls]);

  const handleRoll = useCallback(
    (
      rollKey: string,
      diceRollRequest: RollRequest,
      nextRollKey: string | null
    ): void => {
      onSetRollStatus(rollKey, DataLoadingStatusEnum.LOADING);

      if (tacticalMapsShared3dDiceFlag) {
        const diceNotation = `${diceRollRequest.rolls[0].diceNotation.set
          .map(({ count, dieType }) => `${count}${dieType}`)
          .join("")}`;
        const payload = rollDice(
          {
            action: diceRollRequest.action,
            diceNotation,
            entityId: String(diceRollRequest.context?.entityId),
            entityType: String(diceRollRequest.context?.entityType),
            gameId,
            rollKind: "",
            rollType: diceRollRequest.rolls[0].rollType,
            setId: String(
              useUserDiceDataStore
                .getState()
                .getUserDiceData(Number(diceRollRequest.context?.userId))?.setId
            ),
            userId: String(diceRollRequest.context?.userId),
            avatarUrl: diceRollRequest.context?.avatarUrl,
            name: diceRollRequest.context?.name,
            isShared3dDiceEnabled: useShared3dDiceSettingStore
              .getState()
              .getIsShared3dDiceSettingEnabled(
                true,
                Number(diceRollRequest.context?.userId)
              ),
            operation: DiceOperations.Max,
            operand: diceRollRequest.rolls[0].diceNotation.set[0].operand,
            dmId,
          },
          hasPddErrored,
          isPddReady,
          (eventPayload) =>
            instrumentDiceRoll(
              user,
              eventPayload,
              Platform.Web,
              "characterBuilderRollGroup",
              campaign?.id,
              undefined,
              env
            )
        );

        const rollValues = DiceComponentUtils.generateRollValueContracts(
          payload.data as unknown as RollRequest
        );

        if (!rollValues) {
          throw new Error("error in rollResult values");
        }

        setGroupRolls((prevState) => ({
          ...prevState,
          [payload.data.rollId]: {
            rollKey,
            properties: {
              rollValues,
              rollTotal: payload.data.rolls[0].result.total,
            },
            nextRollKey,
          },
        }));
      } else {
        Dice.roll(diceRollRequest)
          .then((result) => {
            //RollResult | undefined - RollResult is not exported from Dice;
            //  only expect to have one single rollRequest.
            const rollResult = result.rolls[0].result ?? null;

            if (!rollResult) {
              throw new Error("error in rollResult");
            }

            const rollValues =
              DiceComponentUtils.generateRollValueContracts(result);

            if (!rollValues) {
              throw new Error("error in rollResult values");
            }

            //update status
            onSetRollStatus(rollKey, DataLoadingStatusEnum.LOADED);

            //update diceRoll
            onUpdateDiceRoll(
              rollKey,
              { rollValues, rollTotal: rollResult.total },
              nextRollKey
            );
          })
          .catch((error: Error) => {
            //this could handle different errors types from dice package or thrown errors from result

            onSetRollStatus(rollKey, DataLoadingStatusEnum.NOT_LOADED);

            if (onRollError) {
              onRollError(error.message);
            }
            console.log("roll error", error);
          });
      }
    },
    [
      campaign?.id,
      dmId,
      env,
      gameId,
      hasPddErrored,
      isPddReady,
      onRollError,
      onSetRollStatus,
      onUpdateDiceRoll,
      tacticalMapsShared3dDiceFlag,
      user,
    ]
  );

  const handleChange = useCallback(
    (
      rollKey: string,
      newValue: string | null,
      prevValue: string | null,
      nextRollKey: string | null,
      rollTotal: number | null,
      rollValues: Array<RollValueContract>
    ): void => {
      if (newValue === prevValue) {
        return;
      }

      onUpdateDiceRoll(
        rollKey,
        { assignedValue: newValue, rollTotal, rollValues },
        nextRollKey
      );
    },
    []
  );

  const handleConfirm = useCallback((): void => {
    if (onConfirm) {
      return onConfirm(groupKey, diceRolls);
    }
  }, [onConfirm, groupKey, diceRolls]);

  const handleResetGroup = useCallback((): void => {
    if (onResetGroup) {
      onResetGroup(groupKey, diceRolls);
    }

    diceRolls.forEach((roll) => {
      onSetRollStatus(roll.rollKey, DataLoadingStatusEnum.NOT_INITIALIZED);
    });
  }, [onResetGroup, groupKey]);

  const handleRemoveGroup = useCallback((): void => {
    if (onRemoveGroup) {
      onRemoveGroup(groupKey, nextGroupKey);
    }
  }, [onRemoveGroup, groupKey, nextGroupKey]);

  useMessageBroker(
    useCallback(
      (message) => {
        if (!tacticalMapsShared3dDiceFlag) return;

        if (
          isMessage(message) &&
          message.eventType === "dice/roll/fulfilled" &&
          groupRolls[message.data.rollId]
        ) {
          //update status
          onSetRollStatus(
            groupRolls[message.data.rollId].rollKey,
            DataLoadingStatusEnum.LOADED
          );

          //update diceRoll
          onUpdateDiceRoll(
            groupRolls[message.data.rollId].rollKey,
            groupRolls[message.data.rollId].properties,
            groupRolls[message.data.rollId].nextRollKey
          );
        }
      },
      [
        groupRolls,
        onSetRollStatus,
        onUpdateDiceRoll,
        tacticalMapsShared3dDiceFlag,
      ]
    )
  );

  return (
    <div className={classNames.join(" ")}>
      <div className="ddbc-dice-roll-group__actions">
        {showRemoveButton && (
          <RemoveButton
            size="small"
            onClick={handleRemoveGroup}
            enableConfirm={true}
          >
            Delete group
          </RemoveButton>
        )}
      </div>
      <div className="ddbc-dice-roll-group__rolls">
        {diceRolls.map((diceRoll, idx) => {
          const diceRollRequest = generatedRollRequests[idx];
          if (!diceRollRequest) {
            return null;
          }
          const rollStatus = HelperUtils.lookupDataOrFallback(
            rollStatusLookup,
            diceRoll.rollKey,
            DataLoadingStatusEnum.NOT_INITIALIZED
          );

          return (
            <DiceRoll
              key={diceRoll.rollKey}
              rollKey={diceRoll.rollKey}
              nextRollKey={diceRoll.nextRollKey}
              rollTotal={diceRoll.rollTotal}
              rollValues={diceRoll.rollValues}
              assignedValue={diceRoll.assignedValue}
              options={generatedOptions[idx]}
              onChange={handleChange}
              onRoll={handleRoll}
              diceRollRequest={diceRollRequest}
              rollStatus={rollStatus}
            />
          );
        })}
      </div>
      <div className="ddbc-dice-roll-group__actions">
        <RemoveButton
          size="medium"
          style="filled"
          onClick={handleResetGroup}
          disabled={areRollsEmpty}
          enableConfirm={true}
        >
          Reset Group
        </RemoveButton>
        {onConfirm && (
          <Button
            size="medium"
            onClick={handleConfirm}
            disabled={areScoresUnassigned}
          >
            {confirmButtonText}
          </Button>
        )}
      </div>
    </div>
  );
};

export default React.memo(DiceRollGroup);
